// Persistent breadcrumb bar.
//
// Rendered once in the app shell above the routed `<Outlet />`. Reads the
// global navigation trail + the currently-registered page crumb (set by
// each page chrome via `useRegisterPageCrumb`) and renders one
// `<Breadcrumb>`. The bar never unmounts on route changes, so the
// breadcrumb stays visible while the next page is in its loading state —
// no flash.

import { useNavigate } from "react-router";
import { Breadcrumb } from "@ds/display/Breadcrumb";
import type { BreadcrumbItem } from "@ds/display/Breadcrumb";
import { Box } from "@ds/layout/Box";
import { Inline } from "@ds/layout/Inline";
import {
  markNextNavTrailed,
  truncateTrailTo,
  useNavTrail,
  usePageActions,
  usePageCrumb,
  useTrailLocationGuard,
} from "@ds/navigation/navTrail";

export function BreadcrumbBar() {
  const navigate = useNavigate();
  const trail = useNavTrail();
  const page = usePageCrumb();
  const actions = usePageActions();
  // Mount the location guard here so it lives at the persistent shell
  // level — clears the trail on any sider-menu / browser navigation.
  useTrailLocationGuard();

  const items: BreadcrumbItem[] = [];
  trail.forEach((c, i) => {
    items.push({
      label: c.label,
      href: c.href,
      onClick: () => {
        truncateTrailTo(i);
        markNextNavTrailed();
        navigate(c.href);
      },
    });
  });
  if (page) {
    items.push({ label: page.label });
  }

  if (items.length === 0 && !actions) return null;

  return (
    // Crumbs left, page actions right, ONE row. They were two stacked bars —
    // a whole row of vertical space spent on an empty gutter.
    <Box
      tone="primary"
      divider="bottom"
      size="sm"
      paddingX="lg"
      testId="shell-breadcrumb"
    >
      <Inline justify="between" align="center" gap="md" fullWidth>
        <Breadcrumb items={items} />
        {actions}
      </Inline>
    </Box>
  );
}
